// src/pages/management/Versions.tsx

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { debounce } from "lodash";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  MRT_PaginationState,
} from "material-react-table";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import {
  Button,
  Chip,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Back from "../../../../../components/Back";
import FormattedDateCell from "../../../../../components/FormattedDateCell";
import Title from "../../../../../components/Title";
import versionService from "../../../../../services/VersionService";
import { frontVersionInfo } from "../../../../../utils/util";

interface HistoryEntry {
  version: string;
  releaseDate: string;
  features: string[];
}

interface Version {
  id: number;
  service_name: string;
  version_number: string;
  release_date: string;
  current_features: string[];
  history: HistoryEntry[];
  created_at: string;
  updated_at: string;
}

const Versions = () => {
  const [data, setData] = useState<Version[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([
    // {
    //   id: "status",
    //   value: statusType,
    // },
    // {
    //   id: "suite_id",
    //   value: suitId,
    // },
  ]);
  const [globalFilter, setGlobalFilter] = useState("");

  const [openFeatures, setOpenFeatures] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<HistoryEntry[]>([]);
  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      setIsRefetching(true);
      try {
        const resp = await versionService.getAllVersions();
        const versions: Version[] = resp.data;
        setData(versions);
        setRowCount(versions.length);
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
        setIsRefetching(false);
      }
    }, 200),
    []
  );

  useEffect(() => {
    versionService
      .postVersion(frontVersionInfo)
      .catch(console.error)
      .then(() => {
        fetchData();
      });
  }, [fetchData]);

  const columns = useMemo<MRT_ColumnDef<Version>[]>(
    () => [
      {
        accessorKey: "service_name",
        header: "Service",
      },
      {
        accessorKey: "version_number",
        header: "Version",
      },
      {
        accessorKey: "release_date",
        header: "Released",
        Cell: ({ cell }) => (
          <FormattedDateCell value={cell.getValue<string>()} />
        ),
      },
      {
        accessorKey: "current_features",
        header: "Features",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string[]>()?.join("\n") || ""}>
            <Button
              size="small"
              onClick={() => {
                setSelectedFeatures(cell.getValue<string[]>() || []);
                setOpenFeatures(true);
              }}
            >
              View
            </Button>
          </Tooltip>
        ),
      },
      {
        accessorKey: "history",
        header: "History",
        Cell: ({ cell }) => (
          <Button
            size="small"
            onClick={() => {
              setSelectedHistory(cell.getValue<HistoryEntry[]>() || []);
              setOpenHistory(true);
            }}
          >
            View
          </Button>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        Cell: ({ cell }) => (
          <FormattedDateCell value={cell.getValue<string>()} />
        ),
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        Cell: ({ cell }) => (
          <FormattedDateCell value={cell.getValue<string>()} />
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: false,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    manualFiltering: false,

    // enableGlobalFilter: false,
    enableColumnResizing: true,
    manualPagination: false,
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: false,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,

    rowCount,
    state: {
      pagination,
      columnFilters,
      globalFilter,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip arrow title="Refresh Data">
        <IconButton onClick={() => fetchData()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem
        key={1}
        onClick={() => console.log(Number(row.id))}
        sx={{ m: 0 }}
      >
        Edit
      </MenuItem>,
    ],
  });

  return (
    <>
      <Title>Service Versions</Title>
      <MaterialReactTable table={table} />
      <Back />
      <Dialog
        open={openFeatures}
        onClose={() => setOpenFeatures(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Current Features</DialogTitle>
        <DialogContent dividers>
          <List dense>
            {selectedFeatures.map((feat, i) => (
              <ListItem key={i}>
                <Typography>{feat}</Typography>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Version History</DialogTitle>
        <DialogContent dividers>
          <Timeline position="alternate-reverse">
            {selectedHistory.map((entry, idx) => (
              <TimelineItem key={idx}>
                <TimelineSeparator>
                  <TimelineDot />
                  {idx < selectedHistory.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="subtitle1">
                    {entry.version} –{" "}
                    {new Date(entry.releaseDate).toLocaleDateString()}
                  </Typography>
                  <List dense>
                    {entry.features.map((f, j) => (
                      <ListItem key={j}>
                        <Typography variant="body2">• {f}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Versions;
